import { Alert, Button, Checkbox, Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconCircleArrowUpFilled } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { captureEvent } from 'shared/analytics/capture-event';
import type { z } from 'zod/v4';

import {
  PaymentPlanSettingsSchema,
  useLoadPaymentPlanSettings,
  useUpdatePaymentPlanSettings,
} from '../api';

type PaymentPlanFormValues = z.infer<typeof PaymentPlanSettingsSchema>;

const INITIAL_VALUES: PaymentPlanFormValues = { allowPaymentPlans: false };

export const PaymentPlanCard = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const posthog = usePostHog();

  const company = useCompanyConfiguration();
  const canUsePaymentPlans =
    company.package.can_use_payment_plans || company.package.can_use_advanced_payment_plans;

  const { paymentPlanSettings } = useLoadPaymentPlanSettings();
  const { updatePaymentPlanSettings, isUpdatePaymentPlanSettingsLoading } =
    useUpdatePaymentPlanSettings();

  const form = useForm<PaymentPlanFormValues>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(PaymentPlanSettingsSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded settings into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!paymentPlanSettings) return;

    form.setInitialValues(paymentPlanSettings);
    form.setValues(paymentPlanSettings);
  }, [paymentPlanSettings]);

  const handleSave = () => {
    updatePaymentPlanSettings(form.getValues(), { onSuccess: () => form.resetDirty() });
  };

  const handleUpsell = () => {
    captureEvent(posthog, 'upsell:callout_clicked', { feature_name: 'paymentplans' });
    history.push('/upgrade/paymentplans');
  };

  return (
    <Panel
      title={t(i18nKeys.PAYMENT_PLAN.TITLE.MANY)}
      withBorder
      footer={
        canUsePaymentPlans ? (
          <SaveButton
            onClick={handleSave}
            disabled={!form.isDirty()}
            loading={isUpdatePaymentPlanSettingsLoading}
            hideIcon
          />
        ) : undefined
      }
    >
      <Stack my="md">
        {canUsePaymentPlans ? (
          <Checkbox
            label={t(i18nKeys.FORM.PREFERENCES.ALLOW_PAYMENT_PLANS)}
            description={t(i18nKeys.FORM.PREFERENCES.HINT.ALLOW_PAYMENT_PLANS)}
            checked={form.getValues().allowPaymentPlans}
            onChange={event => form.setFieldValue('allowPaymentPlans', event.currentTarget.checked)}
          />
        ) : (
          <Alert variant="light" color="red" icon={<IconCircleArrowUpFilled size={20} />}>
            <Stack align="start" gap="sm">
              {t(i18nKeys.UPSELL.PAYMENT_PLANS.CALLOUT)}
              <Button
                size="xs"
                variant="light"
                color="coral"
                onClick={handleUpsell}
                rightSection={<IconCircleArrowUpFilled size={16} />}
              >
                {t(i18nKeys.UPSELL.PAYMENT_PLANS.CALLOUT_CTA)}
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Panel>
  );
};
